import { Stack } from "@mui/material";

import {
  FillState,
  ObsInput,
  ObsValue,
  ObservationTableSection,
  getFillState,
  TableSections,
} from "@/components/ObservationChart/index";
import { css } from "@/theme";
import { useCallback, useMemo } from "react";
import { useObsTableCtx } from "./ObservationTable_ctx";
import { formatUtc2HospitalDate } from "@/global";
import { obsMetricNames } from "@/resources/observations_metricUtils";

const borderColor = "#9194a5";
const styles = {
  filledCellBase: css`
    --bg-width: 50%/2px;
    --gradient: repeating-linear-gradient(
      to bottom,
      transparent 0 1px,
      black 1px 5px,
      transparent 5px 6px
    );
  `,
  filledCell: {
    none: null,
    startend: null,
    start: css`
      background: var(--gradient) var(--bg-width) 50% no-repeat;
      background-position-y: bottom;
    `,
    end: css`
      background: var(--gradient) var(--bg-width) 50% no-repeat;
      background-position-y: top;
    `,
    continue: css`
      background: var(--gradient) var(--bg-width) 100% no-repeat;
    `,
    [FillState.ORIGIN_VALUE]: null,
  },
  dot: css`
    --dot-size: 10px;

    height: var(--dot-size);
    width: var(--dot-size);
    background-color: #262E54;
    border-radius: 50%;
    border: 1.5px solid #262E54;
  `,
  news: [
    css`
      background-color: #f9f9cc;
    `,
    css`
      background-color: #f5e7ce;
    `,
    css`
      background-color: #ebc5bd;
    `,
    css`
      background-color: #ccc1de;
    `,
  ],
  newsDot: [
    // just make all the dots and lines black rather than coloring them
    css`
      background-color: #000000;
      border-color: #000000;
    `,
    // // This is the yellow line
    // css`
    //   background-color: #AC7811;
    //   border-color: #AC7811;
    // `,
    // // This is the red line
    // css`
    //   background-color: #B5352F;
    //   border-color: #B5352F;
    // `,
    // // This is the purple line
    // css`
    //   background-color: #B5352F;
    //   border-color: #B5352F;
    // `
  ],
  sectionRow: css`
    td {
      border-top: 1px solid ${borderColor};
      border-bottom: 1px solid ${borderColor};
    }
    td:last-child {
      border-right: 1px solid ${borderColor};
    }
  `,
  sectionRowGroupedFirst: css`
    border-top: 2.5px solid ${borderColor};
  `,
  sectionRowGroupedLast: css`
    border-bottom: 2.5px solid ${borderColor};
  `,
  rowLabel: css`
    border: 1px solid ${borderColor};
    text-align: left;
    padding-left: 1rem;
  `,
  originValueTd: css`
    border: 1px solid ${borderColor};
    textAlign: left;
    paddingLeft: 1rem;
  `,
  headingRow: css`
    border: 1px solid ${borderColor};
     div {
      padding-left: 16px;
     }
  `,
  headingRowLong: css`
    min-width: 100px;
    border: 1px solid ${borderColor};
     div {
      padding-left: 16px;
     }
  `,
  headingRowVerticalText: css`
    writing-mode: tb-rl;
    text-orientation: mixed;
    transform: rotate(180deg);
    border: 1px solid ${borderColor};
    // white-space: normal;
    width: 45px;
    min-width: 45px !important;
  `,
  additionalInformationForColumn: css`
     max-width: 300px;
     min-width: 150px;
     position: absolute;
     padding: 4px 16px;
     top: -20px;
     left: 50%;
     transform: translate(-50%, -100%);
     border-radius: 8px;
     background: #444;
     box-shadow: 0px 16px 32px 0px rgba(53, 65, 118, 0.16);
     color: white;
     span:first-child{
      font-weight: bold;
      font-size: 12px;
     };
     p{
      min-width: 200px;
      margin: 2px;
      font-size: 16px;
     }
  `,
};

const formatDateAndTime = (date: Date | string) => formatUtc2HospitalDate(date, "DD/MM/YY @ HH:mm");
// When the user hovers over a column, this component will be shown at the top of the column
const AdditionalObservationInformation = ({ isColumnHovered, isCellInFirstRowInTable }: Pick<ValueCellProps, "isColumnHovered" | "isCellInFirstRowInTable">) => {
  const { getObservationByHoveredElemIndex } = useObsTableCtx();

  const observation = getObservationByHoveredElemIndex();
  if (!observation) return null;

  const displayName = observation.actingAsName ?? observation.createdByName;
  const formattedTime = formatDateAndTime(observation.lastModified);
  const stampText = observation.actingAsName
    ? `${displayName} ${formattedTime} (signed in as ${observation.createdByName})`
    : `${displayName} ${formattedTime}`;

  return isColumnHovered && isCellInFirstRowInTable ? (
    <div css={styles.additionalInformationForColumn}>
      <span>{stampText}</span>
      {observation.note && <p>{observation.note}</p>}
    </div>
  ) : (
    null
  );
};

type ValueCellProps = {
  fillState: FillState;
  news?: number;
  onHover: () => void;
  onHoverOut: () => void;
  isColumnHovered: boolean;
  isCellInFirstRowInTable: boolean;
  isCellIsLastRowInTable: boolean;
  dataKey: ObservationTableSection["dataKey"];
  value: ObsValue;
  drawBorder?: boolean;
  bold?: boolean;
}
// eslint-disable-next-line complexity
const ValueCell = (props: ValueCellProps) => {
  const { fillState, news, onHover, onHoverOut, isColumnHovered, isCellInFirstRowInTable, isCellIsLastRowInTable, dataKey, drawBorder = false, bold = false } = props;

  const hoverOnCell = useCallback(() => {
    onHover();
  }, [onHover]);
  const hoverOut = useCallback(() => {
    onHoverOut();
  }, [onHoverOut]);

  const additionalStyles = useMemo(() => (isColumnHovered ? css`
    border-left: 4px solid #444444;
    border-right: 4px solid #444444;
    position: relative;
    ${isCellInFirstRowInTable ? `
      border-top: 4px solid #444444 !important;
    ` : ""}
    ${isCellIsLastRowInTable ? "border-bottom: 4px solid #444444 !important;" : ""}
    ` : (bold ? css`font-weight: bold;` : "")
    // drawBorder ? css`border-right: 1px solid ${borderColor};` : ""
  ), [isColumnHovered, isCellInFirstRowInTable, isCellIsLastRowInTable]);

  const additionalInformationOnTop = <AdditionalObservationInformation isColumnHovered={isColumnHovered} isCellInFirstRowInTable={isCellInFirstRowInTable} />;
  const showOriginValue = fillState == FillState.ORIGIN_VALUE;

  if (fillState === FillState.NONE) {
    return <td css={showOriginValue ? [styles.originValueTd, additionalStyles] : additionalStyles} onMouseEnter={hoverOnCell} onMouseOut={hoverOut}>
      {additionalInformationOnTop}
    </td>;
  }

  const cellStyles = [
    styles.filledCellBase,
    styles.filledCell[fillState],
    additionalStyles,
  ];

  if (showOriginValue) {
    const modifiedValue =
      typeof props.value === "object"
        ? !!props.value.max || !!props.value.min
          ? `${props.value.max}/${props.value.min}`
          : ""
        : props.value;

    return <td css={[styles.originValueTd, ...cellStyles]} onMouseEnter={hoverOnCell} onMouseOut={hoverOut}>
      {additionalInformationOnTop}
      <span css={{ pointerEvents: "none" }}>{modifiedValue}</span>
    </td>;
  }

  if (fillState === FillState.CONTINUE) {
    return <td css={cellStyles} onMouseEnter={hoverOnCell} onMouseOut={hoverOut}>
      {additionalInformationOnTop}
    </td>;
  }

  const isStartedShouldBeDefault = fillState === FillState.END && dataKey === obsMetricNames.bloodPressure;
  const dotStyles = isStartedShouldBeDefault ? (
    styles.dot
  ) : (
    news ? [styles.dot, styles.newsDot[news - 1]] : styles.dot
  );

  return (
    <td css={cellStyles} onMouseEnter={hoverOnCell} onMouseOut={hoverOut}>
      <Stack direction="row" justifyContent="center">
        <div css={dotStyles}></div>
      </Stack>
      {additionalInformationOnTop}
    </td>
  );
};

type SectionRowProps = {
  input: ObsInput,
  values: ObsValue[],
  blankCells?: React.ReactNode[],
  section: ObservationTableSection,
  isFirstRowInTable: boolean;
  isLastRowInTable: boolean;
  isGrouped: boolean;
  groupOrder?: {
    isFirstItemInGroup: boolean;
    isLastItemInGroup: boolean;
  };
}

const SectionRow = (props: SectionRowProps) => {
  const { input, values, blankCells, section, isFirstRowInTable, isLastRowInTable, isGrouped, groupOrder } = props;

  const notFirstOrLastRowInTable = !isFirstRowInTable && !isLastRowInTable;

  const { value: { hoveredElementIndex, observations }, set, changeHoveredElementIndex } = useObsTableCtx();
  const handleHover = useCallback((index: number) => () => {
    changeHoveredElementIndex(index);
  }, [set, observations]);

  const handleHoverOver = useCallback(() => {
    changeHoveredElementIndex(null);
  }, [set, observations]);

  return (
    <tr
      css={[
        styles.sectionRow,
        input.news && styles.news[input.news - 1],
        (notFirstOrLastRowInTable && isGrouped && groupOrder?.isFirstItemInGroup) ? styles.sectionRowGroupedFirst : null,
        (notFirstOrLastRowInTable && isGrouped && groupOrder?.isLastItemInGroup) ? styles.sectionRowGroupedLast : null,
      ]}
    >
      {input.label && <td css={styles.rowLabel}>{input.label}</td>}

      {values.map((value, index) => {
        return (
          <ValueCell
            key={index}
            fillState={getFillState(value, input)}
            news={input.news}
            onHover={handleHover(index)}
            onHoverOut={handleHoverOver}
            isColumnHovered={index === hoveredElementIndex}
            isCellInFirstRowInTable={isFirstRowInTable}
            isCellIsLastRowInTable={isLastRowInTable}
            dataKey={section.dataKey} value={value}
            drawBorder={(input as any).drawBorder}
            bold={(input as any).bold}
          />
        );
      })}
      {blankCells}
    </tr>
  );
};

type SectionProps = {
  section: ObservationTableSection,
  values: ObsValue[]
  blankCells?: React.ReactNode[],
  tableSections: TableSections;
}

const getConcatGroupName = (secDef: ObservationTableSection) => {

  return secDef.group ? secDef.group + (secDef.groupSubheading ? ` (${secDef.groupSubheading})` : "") : "";
};

const ObservationSection = (props: SectionProps) => {
  const { section, values, blankCells, tableSections = [] } = props;
  const { inputs, group } = section;
  const isGrouped = Boolean(group);
  const concatGroupName = getConcatGroupName(section);

  const isFirstItemInGroup = isGrouped && tableSections.filter((s) => concatGroupName === getConcatGroupName(s))[0] === section;
  const isLastItemInGroup = isGrouped && tableSections.filter((s) => concatGroupName === getConcatGroupName(s)).pop() === section;

  const [isFirstSection, isLastSection] = [
    section.dataKey === tableSections[0].dataKey,
    section.dataKey === tableSections[tableSections.length - 1].dataKey,
  ];
  const noRangeLabels = inputs.every(input => "showOriginValue" in input && input.showOriginValue === true);

  let colSpan = noRangeLabels ? 3 : 2;

  if (isGrouped) {
    colSpan--;
  }

  const rowSpanGroupHeader = (() => {

    if (isGrouped && isFirstItemInGroup) {
      const sectionsInGroup = tableSections.filter((s) => concatGroupName === getConcatGroupName(s)); // these should be consecutive in the config

      let totalInputsInGroup = 0;
      let totalHeadersInGroup = 0;
      for (const itSec of sectionsInGroup) {
        totalInputsInGroup += itSec.inputs.length;
        totalHeadersInGroup++;
      }

      return (totalInputsInGroup + totalHeadersInGroup);
    }

    return 0;
  })();

  return (
    <>
      {/* group row (merge cell) (with the rotated text) - spans over one or more header cells */}
      {
        (isGrouped && isFirstItemInGroup)
          ?
          <tr>
            <td
              css={styles.headingRowVerticalText}
              rowSpan={rowSpanGroupHeader + 1} // and +1 more for the mandatory header row that's coming next
              colSpan={1}
            >
              <Stack
                direction={"column"}
                alignItems={"center"}
                justifyContent={"center"}
                sx={{
                  // just a little bit smaller than the default font size
                  fontSize: "13px",
                }}
              >
                <b>{group}</b>
                {
                  section.groupSubheading
                    ? <i>{section.groupSubheading}</i>
                    : <>&nbsp;</>
                }
              </Stack>
            </td>

          </tr>
          : null
      }
      {/* header row (merge cell) (will bridge multiple rows when more than one input specified ) */}
      <tr>
        <td
          css={styles.headingRow}
          style={{ minWidth: noRangeLabels ? "250px" : undefined }}
          rowSpan={inputs.length + 1}
          colSpan={colSpan}
        >
          <Stack
            direction={"column"}
          >
            <b>{section.heading}</b>
            <i>{section.subheading}</i>
          </Stack>
        </td>
      </tr>
      {/* the values that take up the remainder of the row */}
      {
        inputs.map((input, index) => {
          const isFirstRowInTable = isFirstSection && index === 0;
          const isLastRowInTable = isLastSection && index === inputs.length - 1;

          return (
            <SectionRow
              key={index}
              input={input}
              values={values}
              blankCells={blankCells}
              isFirstRowInTable={isFirstRowInTable}
              isLastRowInTable={isLastRowInTable}
              section={section}
              isGrouped={isGrouped}
              groupOrder={{
                isFirstItemInGroup,
                isLastItemInGroup,
              }}
            />
          );
        })
      }
    </>
  );
};

export default ObservationSection;
